/** Deaths (killing blow attribution, aura cleanup, hooks) and resurrection. */
import type { SimContext, SimEvent, UnitId, Vec2 } from '../contracts';
import { fireProcs, removeInstance } from './auras';
import { resourceDefs } from './resources';
import { emitClean, unitOf } from './util';

/** Last damage event against `id` this tick (killing blow). */
function killingBlow(events: readonly SimEvent[], id: UnitId): { sourceID?: UnitId; abilityGameID?: string } {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (e.type === 'damage' && e.targetID === id) return { sourceID: e.sourceID, abilityGameID: e.abilityGameID };
  }
  return {};
}

/**
 * +basekits: kill credit. Party members on a dead enemy NPC's threat table (the ones who would get
 * its XP) fire their 'killCredit' procs (Bloodprize's window), in unit order.
 */
function creditKill(ctx: SimContext, id: UnitId): void {
  const u = ctx.world.units[id];
  if (!u || u.kind !== 'npc' || u.faction === 'party' || !u.threat) return;
  for (const pid of ctx.world.partyIds) {
    if (u.threat.entries[pid] === undefined) continue;
    fireProcs(ctx, pid, 'killCredit', id, id, 0);
  }
}

export function resolveDeaths(ctx: SimContext): UnitId[] {
  const dead: UnitId[] = [];
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (!u?.alive || u.hp > 0) continue;
    if (u.cast) ctx.sys.casting.cancelCast(ctx, id, 'death');
    u.alive = false;
    u.hp = 0;
    u.diedAt = ctx.now;
    u.cast = null;
    u.queued = null;
    u.move = null;
    u.moving = false;
    u.inCombat = false;
    emitClean(ctx, { type: 'death', targetID: id, ...killingBlow(ctx.world.events, id) });
    for (const inst of [...u.auras]) {
      if (!ctx.content.aura(inst.auraId)?.flags?.persistThroughDeath) removeInstance(ctx, u, inst, 'death');
    }
    for (const z of ctx.world.zones) {
      z.inside = z.inside.filter((x) => x !== id);
      // A dead player's ground effects (Consecrated Ground, Earthquake) end with them; tickZones cleans up.
      if (u.kind === 'player' && z.sourceId === id) {
        z.expiresAt = Math.min(z.expiresAt, ctx.now);
        delete z.onExpire; // +basekits: no end burst from a dead caster
      }
    }
    dead.push(id);
  }
  for (const id of dead) {
    creditKill(ctx, id); // +basekits (before the AI clears the threat table)
    ctx.sys.ai.onDeath(ctx, id);
    ctx.sys.mplus.onDeath(ctx, id);
  }
  return dead;
}

/**
 * +personas: a party member leaves the group (Command 'leaveParty'). Out of play like a death (no
 * actions, off every threat table, its ground effects end) but without a death event or penalty,
 * and never revived. Returns false when the unit can't leave (missing, not a player, already gone).
 */
export function departUnit(ctx: SimContext, unitId: UnitId): boolean {
  const u = unitOf(ctx, unitId);
  if (!u || u.kind !== 'player' || u.departed) return false;
  if (u.cast) ctx.sys.casting.cancelCast(ctx, unitId, 'player');
  u.departed = true;
  delete u.presence;
  u.alive = false;
  u.hp = 0;
  u.diedAt ??= ctx.now;
  u.cast = null;
  u.queued = null;
  u.move = null;
  u.moving = false;
  u.inCombat = false;
  u.targetId = null;
  for (const inst of [...u.auras]) removeInstance(ctx, u, inst, 'cancelled');
  for (const z of ctx.world.zones) {
    z.inside = z.inside.filter((x) => x !== unitId);
    if (z.sourceId === unitId) z.expiresAt = Math.min(z.expiresAt, ctx.now);
  }
  ctx.sys.ai.onDeath(ctx, unitId);
  return true;
}

/** Bring a unit back with a fraction of max HP; resources reset to their starting values. */
export function revive(ctx: SimContext, unitId: UnitId, hpPct: number, pos?: Vec2): void {
  const u = unitOf(ctx, unitId);
  if (!u || u.departed) return; // +personas: a member who left never comes back
  u.alive = true;
  u.diedAt = null;
  u.hp = Math.max(1, Math.min(u.maxHp, Math.round(u.maxHp * hpPct)));
  if (pos) u.pos = { x: pos.x, y: pos.y };
  u.move = null;
  u.moving = false;
  u.cast = null;
  u.queued = null;
  u.inCombat = false;
  u.recentDamage = [];
  if (u.swing) u.swing.nextAt = ctx.now;
  for (const def of resourceDefs(ctx, u)) {
    const pool = u.resources[def.id];
    if (pool) pool.cur = Math.min(pool.max, def.start);
  }
}
